import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { HospitalLocation } from '@/modules/settings/types/hospitalStructure.types'

export interface FlatHospitalLocationOption {
  id: string
  name: string
  depth: number
  label: string
}

export function mapHospitalLocationsToFilterChoices(
  nodes: HospitalLocation[],
  forbiddenIds?: Set<string>,
): IFilterChoiceItem[] {
  return nodes
    .filter(node => !forbiddenIds?.has(node.id))
    .map(node => ({
      id: node.id,
      label: node.number ? `${node.name} (${node.number})` : node.name,
      children: mapHospitalLocationsToFilterChoices(node.children || [], forbiddenIds),
    }))
}

/** Клиентская фильтрация дерева по подстроке (с сохранением ветки при матче в детях). */
export function filterChoiceTreeBySearch(
  nodes: IFilterChoiceItem[],
  search: string,
): IFilterChoiceItem[] {
  const query = search.trim().toLowerCase()
  if (!query) {
    return nodes
  }

  const result: IFilterChoiceItem[] = []
  for (const node of nodes) {
    const children = filterChoiceTreeBySearch(node.children || [], search)
    const selfMatch = String(node.label).toLowerCase().includes(query)
    if (selfMatch || children.length) {
      result.push({
        ...node,
        children,
      })
    }
  }
  return result
}

export function collectDescendantIds(node: HospitalLocation, target = new Set<string>()): Set<string> {
  for (const child of node.children || []) {
    target.add(child.id)
    collectDescendantIds(child, target)
  }
  return target
}

export function findLocationInTree(
  nodes: HospitalLocation[],
  id: string,
): HospitalLocation | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node
    }
    const nested = findLocationInTree(node.children || [], id)
    if (nested) {
      return nested
    }
  }
  return null
}

export function getForbiddenParentIds(
  tree: HospitalLocation[],
  locationId: string | null | undefined,
): Set<string> {
  const forbidden = new Set<string>()
  if (!locationId) {
    return forbidden
  }

  forbidden.add(locationId)
  const node = findLocationInTree(tree, locationId)
  if (node) {
    collectDescendantIds(node, forbidden)
  }
  return forbidden
}

export function flattenHospitalTree(
  nodes: HospitalLocation[],
  depth = 0,
  result: FlatHospitalLocationOption[] = [],
): FlatHospitalLocationOption[] {
  for (const node of nodes) {
    const prefix = depth > 0 ? `${'— '.repeat(depth)}` : ''
    result.push({
      id: node.id,
      name: node.name,
      depth,
      label: `${prefix}${node.name}`,
    })
    if (node.children?.length) {
      flattenHospitalTree(node.children, depth + 1, result)
    }
  }
  return result
}
